// Quick Admin Setup Utility
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { MigrationDataAccessLayer } from '../services/migrationDataAccessLayer';
import Logger from '../services/logger';

/**
 * Quick function to make the current logged-in user an admin
 * This can be run from the browser console for easy admin setup
 */
export const quickAdminSetup = async (): Promise<void> => {
  try {
    const auth = getAuth();
    
    return new Promise((resolve, reject) => {
      onAuthStateChanged(auth, async (user) => {
        if (!user) {
          reject(new Error('No user logged in'));
          return;
        }

        try {
          // Update user document with admin privileges using dual-mode access
          const migrationDataAccess = new MigrationDataAccessLayer();
          const result = await migrationDataAccess.updateUserProfile(user.uid, {
            isAdmin: true,
            isSuperAdmin: true,
            userType: 'admin',
            subscriptionTier: 'admin',
            companyName: 'System Administrator',
            role: 'admin',
            tierAssignedAt: new Date(),
            tierAssignedBy: 'system',
            tierAssignmentReason: 'Quick admin setup',
            updatedAt: new Date()
          });
          
          if (result.success) {
            resolve();
          } else {
            throw new Error(result.error || 'Failed to update user profile');
          }
        } catch (error) {
          Logger.error('❌ Admin setup failed:', error instanceof Error ? error : new Error(String(error)), { error });
          reject(error);
        }
      });
    });
  } catch (error) {
    Logger.error('Quick admin setup failed:', error instanceof Error ? error : new Error(String(error)), { error });
    throw error;
  }
};

/**
 * Check if current user has admin privileges
 */
export const checkAdminStatus = async (): Promise<{
  isAdmin: boolean;
  isSuperAdmin: boolean;
  userEmail: string | null;
}> => {
  try {
    const auth = getAuth();
    
    return new Promise((resolve) => {
      onAuthStateChanged(auth, async (user) => {
        if (!user) {
          resolve({ isAdmin: false, isSuperAdmin: false, userEmail: null });
          return;
        }

        try {
          const migrationDataAccess = new MigrationDataAccessLayer();
          const result = await migrationDataAccess.getUserProfile(user.uid);
          
          if (result.success && result.data) {
            const userData = result.data;
            const adminResult = {
              isAdmin: userData.isAdmin || false,
              isSuperAdmin: userData.isSuperAdmin || false,
              userEmail: user.email
            };
            resolve(adminResult);
          } else {
            resolve({ isAdmin: false, isSuperAdmin: false, userEmail: user.email });
          }
        } catch (error) {
          Logger.error('Error checking admin status:', error instanceof Error ? error : new Error(String(error)), { error });
          resolve({ isAdmin: false, isSuperAdmin: false, userEmail: user.email });
        }
      });
    });
  } catch (error) {
    Logger.error('Check admin status failed:', error instanceof Error ? error : new Error(String(error)), { error });
    return { isAdmin: false, isSuperAdmin: false, userEmail: null };
  }
};

// Make functions available in browser console for easy access
if (typeof window !== 'undefined') {
  (window as any).quickAdminSetup = quickAdminSetup;
  (window as any).checkAdminStatus = checkAdminStatus;
  }
